/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {RenderDocument} from "js_renderer";

import AbstractModule from "../abstract_module";
import {vec2, mat4} from "gl-matrix";
import ISaveModule from "./scene_save_interface";
import Scene from "../../scene";
import Settings from "../settings";
import {template} from "./model_page_template";

export interface ISaveSettings {
    name: string;
    viaBrowserSavePageAs: boolean;
    // what else?
}

export default
class SaveModule extends AbstractModule implements ISaveModule {

    protected static _typeName: string = "save";

    private static addModelMagicString: string = "<<REPLACE_THIS_LINE_IN_LEXOCAD_EXPORT_TO_LOCAL_MODEL_MAGIC_STRING>>";
    private static addLocalModelForSaveToDOMMagicString: string = "<<ADD_LOCAL_MODEL_FOR_SAVE_TO_DOM_MAGIC_STRING>>";

    protected _defaultSettings: ISaveSettings;

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);

        this._type = SaveModule._typeName;

        this._defaultSettings = {
            name: "saveModel" + new Date().toDateString(),
            viaBrowserSavePageAs: false
        };

        this._settings = Settings.clone(this._defaultSettings);
    }

    public static getClassType(): string {
        return SaveModule._typeName;
    }

    public viewChanged() {
    }

    public viewResized(aContentSize: vec2) {
    }

    public generateData(aApplicationName: string): string {

        let content = {
            value: ""
        };
        let templateLines = window.atob(template).split("\n");

        let lineReplaces = this.getLineReplaces();

        for (var l = 0; l < templateLines.length; l++) {
            let inputLine = templateLines[l];

            let lineReplacement = this.replaceTemplateLine(templateLines[l], lineReplaces);
            if (lineReplacement !== null) {
                content.value += lineReplacement + "\n";
            }
            else {
                if (inputLine.indexOf("app1 = new cadwork.webgl") !== -1) {
                    content.value += "\t\t\t app1 = new cadwork.webgl." + aApplicationName + "()\n";
                }
                else if (inputLine.indexOf(SaveModule.addLocalModelForSaveToDOMMagicString) !== -1) {
                    let currTrafo: mat4 = this.sceneAPI.manipulatorModule.getStoredTransformation();
                    if (currTrafo) {
                        // new trafo
                        content.value += "\t\t\t app1.addModel(\"geometryDescriptionFor_scene1\", \"modelDescriptionFor_scene1\",[" + currTrafo.toString() + "]);//" + SaveModule.addLocalModelForSaveToDOMMagicString + "\n";
                    }
                    else {
                        // we should look for the trafo in the DOM
                        let domTrafoString: string|null = this.parseModelTransformationFromMainScript();
                        if (domTrafoString) {
                            // not changed trafo
                            content.value += "\t\t\t app1.addModel(\"geometryDescriptionFor_scene1\", \"modelDescriptionFor_scene1\", " + domTrafoString + ");//" + SaveModule.addLocalModelForSaveToDOMMagicString + "\n";
                        }
                        else {
                            // no trafo
                            content.value += "\t\t\t app1.addModel(\"geometryDescriptionFor_scene1\", \"modelDescriptionFor_scene1\");//" + SaveModule.addLocalModelForSaveToDOMMagicString + "\n";
                        }
                    }
                }
                else if (inputLine.indexOf("app1.setConfiguration") !== -1) {
                    content.value += "app1.setConfiguration(\"applicationDescriptionFor_scene1\");" + "\n";
                }
                else if (inputLine.indexOf("</body>") !== -1) { // this is the div with base64 encoded data (files)

                    this.assemblyConfigurationToLocalModel("scene1", content);
                    this.assemblyModelDescriptionToLocalModel("scene1", content);
                    this.assemblyGeometryDescriptionToLocalModel("scene1", content);
                    this.assemblyApplicationDescriptionToLocalModel("scene1", content);
                    this.assemblyTranslationsToLocalModel(content);

                    this.assemblyShapesToLocalModel("scene1", content);
                    this.assemblyTexturesToLocalModel("scene1", content);
                    this.assemblyPointCloudsToLocalModel("scene1", content);
                    this.assemblyFontsToLocalModel("scene1", content);
                    this.assemblyAnimationsToLocalModel("scene1", content);
                    this.assemblyImagesToLocalModel("scene1", content);

                    this.assemblyTerrainToLocalModel(content);

                    this.assemblyLibrariesToModel("scene1", content);
                    /*
                    // TODO: the cwImage is in different div than the imagesFor_scene1, but probably this is not important
                    assemblyBackGroundImageToModel(tempDirectoryWithBinaryData + QString(CDWK_IMAGES_OUTPUT_DIRECTORY) + QString("/"), "scene1");
                    assemblyCustomLogoImageToModel(tempDirectoryWithBinaryData + QString(CDWK_IMAGES_OUTPUT_DIRECTORY) + QString("/"), "scene1");
                    */

                    content.value += inputLine;
                    content.value += "\n";
                }
                else {
                    content.value += inputLine;
                    content.value += "\n";
                }
            }
        }

        return content.value;
    }

    private parseModelTransformationFromMainScript(): string | null {
        let script = document.getElementById('mainScript');
        if (script) {
            // look if some transformation is already included here
            let lines = script.innerHTML.split("\n");

            for (let l = 0; l < lines.length; l++) {
                let line = lines[l];

                let index = line.indexOf(SaveModule.addLocalModelForSaveToDOMMagicString);
                if (index !== -1) {
                    // search for string addModel
                    index = line.indexOf("app1.addModel(\"");
                    if (index !== -1) {
                        let searchTrafoBeginString = "[";
                        let trafoBeginIndex = line.indexOf(searchTrafoBeginString);
                        if (trafoBeginIndex !== -1) {
                            let searchTrafoEndString = "]);";
                            let trafoEndIndex = line.indexOf(searchTrafoEndString);
                            let trafoString = line.substr(trafoBeginIndex, trafoEndIndex - trafoBeginIndex);
                            trafoString += "]";

                            // let parsedTrafo = mat4.create();
                            // parsedTrafo = JSON.parse(trafoString);
                            //
                            // return parsedTrafo;
                            return trafoString;
                        }
                    }
                }
            }
        }

        return null;
    }


    private assemblyTerrainToLocalModel(aContent: {value: string}) {

        // todo crete some static property with the terrain name in scene api, now the terrain model name is created in application module
        let terrainModelName = "Terrain Model";

        let modelDIVElement = document.getElementById(terrainModelName);
        if (!modelDIVElement) {
            return;
        }

        aContent.value += '<div id=\"' + terrainModelName + '\">' + '\n';
        this.assemblyTerrainModelDescriptionToLocalModel(terrainModelName, aContent);
        this.assemblyTerrainGeometryDescriptionToLocalModel(terrainModelName, aContent);
        this.assemblyTerrainShapesToLocalModel(terrainModelName, aContent);
        aContent.value += '<\/div>' + '\n';
    }

    private assemblyTerrainModelDescriptionToLocalModel(aModelId: string, aContent: {value: string}) {
        aContent.value += "<div id=\"modelDescriptionFor_" + aModelId + "\" content=\"" + document.getElementById("modelDescriptionFor_" + aModelId).getAttribute("content") + "\"><\/div>" + "\n";
    }

    private assemblyTerrainGeometryDescriptionToLocalModel(aModelId: string, aContent: {value: string}) {
        aContent.value += "<div id=\"geometryDescriptionFor_" + aModelId + "\" content=\"" + document.getElementById("geometryDescriptionFor_" + aModelId).getAttribute("content") + "\"><\/div>" + "\n";
    }

    private assemblyTerrainShapesToLocalModel(aModelId: string, aContent: {value: string}) {

        // find all cwShape elements
        let cwShapes: HTMLCollectionOf<Element> = document.getElementsByTagName("cwShapeGeometry");

        aContent.value += "<div id=\"shapesFor_" + aModelId + "\" style=\"display: none;\">" + "\n";

        for (let i = 0; i < cwShapes.length; i++) {
            let cwShape = cwShapes[i];
            aContent.value += "<cwShapeGeometry id=\"" + cwShape.getAttribute('id') + "\" base64data=\"" + cwShape.getAttribute('base64data') + "\">" + "</cwShapeGeometry>\n";
        }

        aContent.value += "</div>" + "\n";
    }


    private assemblyShapesToLocalModel(aModelId: string, aContent: {value: string}) {

        // find all cwShape elements
        let cwShapes: HTMLCollectionOf<Element> = document.getElementsByTagName("cwShape");

        aContent.value += "<div id=\"shapesFor_" + aModelId + "\" style=\"display: none;\">" + "\n";

        for (let i = 0; i < cwShapes.length; i++) {
            let cwShape = cwShapes[i];
            aContent.value += "<cwShape id=\"" + cwShape.getAttribute('id') + "\" base64data=\"" + cwShape.getAttribute('base64data') + "\">" + "</cwShape>\n";
        }

        aContent.value += "</div>" + "\n";
    }

    private assemblyTexturesToLocalModel(aModelId: string, aContent: {value: string}) {

        // find all cwShape elements
        let cwTexturebatches: HTMLCollectionOf<Element> = document.getElementsByTagName("cwTextureBatches");

        aContent.value += "<div id=\"texturesFor_" + aModelId + "\" style=\"display: none;\">" + "\n";

        for (let i = 0; i < cwTexturebatches.length; i++) {
            let cwTextureBatch = cwTexturebatches[i];
            aContent.value += "<cwTextureBatches id=\"" + cwTextureBatch.getAttribute('id') + "\" base64data=\"" + cwTextureBatch.getAttribute('base64data') + "\">" + "</cwTextureBatches>\n";
        }

        aContent.value += "</div>" + "\n";
    }

    private assemblyPointCloudsToLocalModel(aModelId: string, aContent: {value: string}) {

        // find all cwShape elements
        let cwPointClouds: HTMLCollectionOf<Element> = document.getElementsByTagName("cwPointCloud");

        aContent.value += "<div id=\"pointCloudsFor_" + aModelId + "\" style=\"display: none;\">" + "\n";

        for (let i = 0; i < cwPointClouds.length; i++) {
            let cwPointCloud = cwPointClouds[i];
            aContent.value += "<cwPointCloud id=\"" + cwPointCloud.getAttribute('id') + "\" base64data=\"" + cwPointCloud.getAttribute('base64data') + "\">" + "</cwPointCloud>\n";
        }

        aContent.value += "</div>" + "\n";
    }

    private assemblyFontsToLocalModel(aModelId: string, aContent: {value: string}) {

        // find all cwShape elements
        let cwFonts: HTMLCollectionOf<Element> = document.getElementsByTagName("cwFont");

        aContent.value += "<div id=\"fontsFor_" + aModelId + "\" style=\"display: none;\">" + "\n";

        for (let i = 0; i < cwFonts.length; i++) {
            let cwFont = cwFonts[i];
            aContent.value += "<cwFont id=\"" + cwFont.getAttribute('id') + "\" base64data=\"" + cwFont.getAttribute('base64data') + "\">" + "</cwFont>\n";
        }

        aContent.value += "</div>" + "\n";
    }

    private assemblyAnimationsToLocalModel(aModelId: string, aContent: {value: string}) {

        // find all cwShape elements
        let cwAnimations: HTMLCollectionOf<Element> = document.getElementsByTagName("cwAnimation");

        aContent.value += "<div id=\"animationsFor_" + aModelId + "\" style=\"display: none;\">" + "\n";

        for (let i = 0; i < cwAnimations.length; i++) {
            let cwAnimation = cwAnimations[i];
            aContent.value += "<cwAnimation id=\"" + cwAnimation.getAttribute('id') + "\" base64data=\"" + cwAnimation.getAttribute('base64data') + "\">" + "</cwAnimation>\n";
        }

        aContent.value += "</div>" + "\n";
    }

    private assemblyImagesToLocalModel(aModelId: string, aContent: {value: string}) {

        // find all cwShape elements
        let cwImages: HTMLCollectionOf<Element> = document.getElementsByTagName("cwImages");

        aContent.value += "<div id=\"imagesFor_" + aModelId + "\" style=\"display: none;\">" + "\n";

        for (let i = 0; i < cwImages.length; i++) {
            let cwImage = cwImages[i];
            aContent.value += "<cwImages id=\"" + cwImage.getAttribute('id') + "\" base64data=\"" + cwImage.getAttribute('base64data') + "\">" + "</cwImages>\n";
        }

        aContent.value += "</div>" + "\n";
    }

    private assemblyLibrariesToModel(aModelId: string, aContent: {value: string}) {

        // find all cwShape elements
        let cwLibs: HTMLCollectionOf<Element> = document.getElementsByTagName("cwLib");

        aContent.value += "<div id=\"libsFor_" + aModelId + "\" style=\"display: none;\">" + "\n";

        for (let i = 0; i < cwLibs.length; i++) {
            let cwLib = cwLibs[i];
            aContent.value += "<cwLib id=\"" + cwLib.getAttribute('id') + "\" base64data=\"" + cwLib.getAttribute('base64data') + "\">" + "</cwLib>\n";
        }

        aContent.value += "</div>" + "\n";
    }

    private assemblyConfigurationToLocalModel(aModelId: string, aContent: {value: string}) {
        let configuration = this.sceneAPI.getConfiguration();
        aContent.value += '<cwSettings id=\"globalSettings\" base64data=\"' + window.btoa(JSON.stringify(configuration)) + '\"<\/cwSettings>' + '\n';
    }

    private assemblyModelDescriptionToLocalModel(aModelId: string, aContent: {value: string}) {
        if (document.getElementById("modelDescriptionFor_" + aModelId)) {
            aContent.value += '<script id=\"modelDescriptionFor_' + aModelId + '\" type=\"application/json\">' + document.getElementById("modelDescriptionFor_" + aModelId).innerText + '<\/script>' + '\n';
        }
    }

    private assemblyGeometryDescriptionToLocalModel(aModelId: string, aContent: {value: string}) {
        if (document.getElementById("geometryDescriptionFor_" + aModelId)) {
            aContent.value += "<script id=\"geometryDescriptionFor_" + aModelId + "\" type=\"application/json\">" + document.getElementById("geometryDescriptionFor_" + aModelId).innerText + "<\/script>" + "\n";
        }
    }

    private assemblyApplicationDescriptionToLocalModel(aModelId: string, aContent: {value: string}) {
        if (document.getElementById("applicationDescriptionFor_" + aModelId)) {
            aContent.value += "<script id=\"applicationDescriptionFor_" + aModelId + "\" type=\"application/json\">" + document.getElementById("applicationDescriptionFor_" + aModelId).innerText + "<\/script>" + "\n";
        }
    }

    private assemblyTranslationsToLocalModel(aContent: {value: string}) {

        let translations = this.getTranslations();

        for (let i = 0; i < translations.length; i++) {
            aContent.value += "<script id=\"" + translations[i].key + "\" type=\"text/plain\">" + translations[i].value + "<\/script>" + "\n";
        }
    }

    private replaceTemplateLine(aLine: string, aLineReplaces: {key: string, value:string}[]): string | null {
        for (let i = 0; i < aLineReplaces.length; i++) {
            if (aLine.lastIndexOf(aLineReplaces[i].key) !== -1) {
                return aLineReplaces[i].value;
            }
        }

        return null;
    }

    private getLineReplaces(): {key: string, value:string}[] {
        let lineReplaces = [];

        if (document.getElementById("renderer_css")) {
            lineReplaces.push({
                key: "addScripts('renderer', 'css')",
                value: "<style id=\"renderer_css\">\n" + document.getElementById("renderer_css").innerText + "</style>\n"
            });
        }

        if (document.getElementById("app_css")) {
            lineReplaces.push({
                key: "addScripts('app', 'css')",
                value: "<style id=\"app_css\">\n" + document.getElementById("app_css").innerText + "</style>\n"
            });
        }

        if ( document.getElementById("debug_library_script")) {
            lineReplaces.push({
                key: "addScripts('appLibs', 'js')", value: "" +
                    "<script id=\"debug_library_script\" type=\"text/javascript\">\n" + document.getElementById("debug_library_script").innerText + "\n" + "<\/script>\n" +
                    "<script id=\"jquery_library_script\" type=\"text/javascript\">\n" + document.getElementById("jquery_library_script").innerText + "\n" + "<\/script>\n" +
                    "<script id=\"jquery_min_library_script\" type=\"text/javascript\">\n" + document.getElementById("jquery_min_library_script").innerText + "\n" + "<\/script>\n" +
                    "<script id=\"download_script\" type=\"text/javascript\">\n" + document.getElementById("download_script").innerText + "\n" + "<\/script>\n" +
                    "<script id=\"jscolor_library_script\" type=\"text/javascript\">\n" + document.getElementById("jscolor_library_script").innerText + "<\/script>\n"
            });
        }

        if (document.getElementById("app_script")) {
            lineReplaces.push({
                key: "addScripts('app', 'js')",
                value: "<script id=\"app_script\" type=\"text/javascript\">\n" + document.getElementById("app_script").innerText + "<\/script>\n"
            });
        }

        if (document.getElementById("jsrenderer_script")) {
            lineReplaces.push({
                key: "addScripts('renderer', 'js')",
                value: "<script id=\"jsrenderer_script\" type=\"text/javascript\">\n" + document.getElementById("jsrenderer_script").innerText + "<\/script>\n"
            });
        }

        if (document.getElementById("jsinterface_script")) {
            lineReplaces.push({
                key: "addScripts('interface', 'js')",
                value: "<script id=\"jsinterface_script\" type=\"text/javascript\">\n" + document.getElementById("jsinterface_script").innerText + "<\/script>\n"
            });
        }

        return lineReplaces;
    }

    private getTranslations(): {key: string, value:string}[] {
        let translations = [];

        if (document.getElementById("app_translation_sp")) {
            translations.push({
                key: "app_translation_sp",
                value: document.getElementById("app_translation_sp").innerText
            });
        }

        if (document.getElementById("app_translation_it")) {
            translations.push({
                key: "app_translation_it",
                value: document.getElementById("app_translation_it").innerText
            });
        }

        if (document.getElementById("app_translation_ru")) {
            translations.push({
                key: "app_translation_ru",
                value: document.getElementById("app_translation_ru").innerText
            });
        }

        if (document.getElementById("app_translation_fr")) {
            translations.push({
                key: "app_translation_fr",
                value: document.getElementById("app_translation_fr").innerText
            });
        }

        if (document.getElementById("app_translation_en")) {
            translations.push({
                key: "app_translation_en",
                value: document.getElementById("app_translation_en").innerText
            });
        }

        if (document.getElementById("app_translation_de")) {
            translations.push({
                key: "app_translation_de",
                value: document.getElementById("app_translation_de").innerText
            });
        }

        if (document.getElementById("app_translation_cz")) {
            translations.push({
                key: "app_translation_cz",
                value: document.getElementById("app_translation_cz").innerText
            });
        }

        return translations;
    }
}

Scene.registerSceneAPIModule(SaveModule);
